import { searchKey } from '@natashu/shared';
import { ContactRound, CornerDownLeft, KeyRound, Search, ShieldCheck, UsersRound, type LucideIcon } from 'lucide-react';
import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { TextInput } from '../components/ui/Field.tsx';
import { Modal } from '../components/ui/Modal.tsx';
import { ProviderIcon } from '../components/ui/ProviderIcon.tsx';
import { RECORD_SEARCH_MIN_CHARS, useRecordSearch } from '../features/crm/useRecordSearch.ts';
import { useAuth } from '../lib/auth.tsx';
import { cx } from '../lib/cx.ts';
import { visibleNavGroups } from '../lib/permissions.ts';
import { NAV_GROUPS } from './nav.ts';

interface PaletteEntry {
  key: string;
  to: string;
  label: string;
  description?: string;
  group: string;
  icon?: LucideIcon;
  /** Custom leading mark (e.g. channel icon); replaces `icon`. */
  mark?: ReactNode;
  demo?: boolean;
}

export interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
  /** Optional extra slot rendered below the results. */
  renderResults?: (query: string, close: () => void) => ReactNode;
}

const ACCOUNT_ENTRIES: readonly PaletteEntry[] = [
  { key: 'account-security', to: '/account/security', label: 'ความปลอดภัยบัญชี (2FA)', group: 'บัญชีของฉัน', icon: ShieldCheck },
  { key: 'account-password', to: '/account/password', label: 'เปลี่ยนรหัสผ่าน', group: 'บัญชีของฉัน', icon: KeyRound },
];

/** Ctrl/⌘+K: jump to any page the user can open, or search contacts, teams and chats. */
export function CommandPalette({ open, onClose, renderResults }: CommandPaletteProps) {
  const { permissions } = useAuth();
  const navigate = useNavigate();
  const listId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const search = useRecordSearch(query, open);

  const entries = useMemo<PaletteEntry[]>(
    () => [
      ...visibleNavGroups(NAV_GROUPS, permissions).flatMap((g) => g.items.filter((i) => !i.hidden).map((i) => ({ key: i.key, to: i.to, label: i.label, description: i.description, group: g.label, icon: i.icon }))),
      ...ACCOUNT_ENTRIES,
    ],
    [permissions],
  );

  const pages = useMemo(() => {
    const q = searchKey(query);
    if (!q) return entries;
    return entries.filter((e) => searchKey(`${e.label} ${e.group} ${e.description ?? ''} ${e.to}`).includes(q));
  }, [entries, query]);

  const records = useMemo<PaletteEntry[]>(() => {
    const data = search.data;
    if (!data) return [];
    const out: PaletteEntry[] = [];
    for (const c of data.contacts.items) {
      out.push({
        key: `contact-${c.id}`,
        to: `/contacts/${c.id}`,
        label: c.displayName,
        description: [c.coachName, c.teamNames.join(', '), c.province, c.primaryPhone?.display].filter(Boolean).join(' · '),
        group: 'ผู้ติดต่อ',
        icon: ContactRound,
        demo: c.isDemo,
      });
    }
    if (data.contacts.hasMore) {
      out.push({ key: 'contact-more', to: `/contacts?q=${encodeURIComponent(data.q)}`, label: `ดูผู้ติดต่อทั้งหมดที่ตรงกับ “${data.q}”`, group: 'ผู้ติดต่อ', icon: Search });
    }
    for (const t of data.teams.items) {
      out.push({
        key: `team-${t.id}`,
        to: `/teams/${t.id}`,
        label: t.name,
        description: [t.province, t.matchedAlias ? `ชื่อเรียก: ${t.matchedAlias}` : null, t.archivedAt ? 'เก็บถาวร' : null].filter(Boolean).join(' · '),
        group: 'ทีม',
        icon: UsersRound,
      });
    }
    if (data.teams.hasMore) {
      out.push({ key: 'team-more', to: `/teams?q=${encodeURIComponent(data.q)}`, label: `ดูทีมทั้งหมดที่ตรงกับ “${data.q}”`, group: 'ทีม', icon: Search });
    }
    for (const cv of data.conversations.items) {
      out.push({
        key: `conversation-${cv.id}`,
        to: `/inbox/${cv.id}`,
        label: cv.contact?.displayName ?? cv.identityDisplayName ?? 'แชท',
        description: cv.snippet?.text ?? [cv.identityDisplayName, cv.needsReply ? 'รอตอบ' : null, cv.status === 'closed' ? 'ปิดเรื่องแล้ว' : null].filter(Boolean).join(' · '),
        group: 'แชท/ข้อความ',
        mark: <ProviderIcon provider={cv.provider} size={22} demoTag={false} />,
        demo: cv.isDemo,
      });
    }
    return out;
  }, [search.data]);

  const results = useMemo(() => [...pages, ...records], [pages, records]);

  useEffect(() => {
    if (open) {
      setQuery('');
      setActive(0);
    }
  }, [open]);

  useEffect(() => {
    setActive(0);
  }, [query]);

  useEffect(() => {
    if (active >= results.length && results.length > 0) setActive(results.length - 1);
  }, [active, results.length]);

  useEffect(() => {
    document.getElementById(`${listId}-${active}`)?.scrollIntoView({ block: 'nearest' });
  }, [active, listId]);

  const go = (entry: PaletteEntry | undefined) => {
    if (!entry) return;
    onClose();
    navigate(entry.to);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (results.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => (i + 1) % results.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => (i - 1 + results.length) % results.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      go(results[active]);
    }
  };

  const trimmed = query.trim();
  const tooShort = trimmed.length > 0 && Array.from(trimmed).length < RECORD_SEARCH_MIN_CHARS;
  const searching = search.active && (search.isFetching || !search.settled);
  let lastGroup = '';

  return (
    <Modal open={open} onClose={onClose} title="ค้นหาและไปยังหน้า" size="md" initialFocus={inputRef} className="palette">
      <div className="palette__search">
        <TextInput
          ref={inputRef}
          icon={Search}
          label="ค้นหาหน้า ผู้ติดต่อ ทีม หรือแชท"
          labelHidden
          placeholder="พิมพ์ชื่อหน้า ผู้ติดต่อ ทีม หรือเบอร์บางส่วน"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onKeyDown}
          role="combobox"
          aria-expanded="true"
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={results.length ? `${listId}-${active}` : undefined}
          autoComplete="off"
          spellCheck={false}
        />
      </div>
      <div className="palette__list" id={listId} role="listbox" aria-label="ผลการค้นหา" aria-busy={searching || undefined}>
        {results.length === 0 && !searching && trimmed && !tooShort && <p className="palette__empty">ไม่พบผลลัพธ์สำหรับ “{query}”</p>}
        {results.length === 0 && tooShort && <p className="palette__empty">ไม่พบหน้าที่ตรงกับ “{query}”</p>}
        {results.map((entry, index) => {
          const showGroup = entry.group !== lastGroup;
          lastGroup = entry.group;
          const Icon = entry.icon;
          return (
            <div key={entry.key} role="presentation">
              {showGroup && (
                <p className="palette__group" role="presentation">
                  {entry.group}
                </p>
              )}
              <div
                id={`${listId}-${index}`}
                role="option"
                aria-selected={index === active}
                className={cx('palette__option')}
                onPointerMove={() => setActive(index)}
                onClick={() => go(entry)}
              >
                <span className="palette__option-icon" aria-hidden="true">
                  {entry.mark ?? (Icon ? <Icon size={18} strokeWidth={1.75} /> : null)}
                </span>
                <span className="palette__option-text">
                  <span className="palette__option-label">
                    {entry.label}
                    {entry.demo && <span className="provider__demo" style={{ marginLeft: 6 }}>DEMO</span>}
                  </span>
                  {entry.description && <span className="palette__option-desc">{entry.description}</span>}
                </span>
                {index === active && <CornerDownLeft className="palette__option-enter" size={16} strokeWidth={1.75} aria-hidden="true" />}
              </div>
            </div>
          );
        })}
        {tooShort && <p className="palette__group">พิมพ์อย่างน้อย {RECORD_SEARCH_MIN_CHARS} ตัวอักษรเพื่อค้นหาผู้ติดต่อ ทีม และแชท</p>}
        {searching && (
          <p className="palette__group" role="status">
            กำลังค้นหาข้อมูล…
          </p>
        )}
        {search.isError && !searching && <p className="palette__group">ค้นหาข้อมูลไม่สำเร็จ ลองพิมพ์ใหม่อีกครั้ง</p>}
        {renderResults?.(query, onClose)}
      </div>
      <div className="palette__hint" aria-hidden="true">
        <span>
          <kbd>↑</kbd> <kbd>↓</kbd> เลือก
        </span>
        <span>
          <kbd>Enter</kbd> เปิด
        </span>
        <span>
          <kbd>Esc</kbd> ปิด
        </span>
      </div>
    </Modal>
  );
}
